import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Modal, Row, Col, Divider, Select, Input, Form, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import TableComponent from 'components/table-component';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { API_STATUS } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { AiOutlineSearch } from 'react-icons/ai';
import { putApi } from 'redux/sagas/putApiSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import './period-model.scss';
import { formQueryStringFromObject } from 'helpers';
import { useI18n } from '../../hooks/useI18n';

const layer1FormCol = {
	labelCol: {
		span: 24,
	},
	wrapperCol: {
		span: 24,
	},
};

const PeriodModal = ({ value = null, onChange }) => {
	const [periodModal, setPeriodModal] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [addFormVisible, setAddFormVisible] = useState(false);
	const [editData, setEditData] = useState(null);
	const [tableData, setTableData] = useState([]);
	const selectRef = useRef(null);
	const globalRedux = useSelector((state) => state.globalRedux);
	const periods = useSelector((state) => state?.periodRedux?.periods);
	console.log('🚀 ~ PeriodModal ~ periods:', globalRedux, periods);
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const { t } = useI18n();
	const column = [
		{
			title: t('periods.columns.period_name'),
			dataIndex: 'name',
			key: 'name',
			width: '20%',
			align: 'left',
		},
		{
			title: t('periods.columns.days'),
			dataIndex: 'days',
			key: 'days',
			width: '20%',
			align: 'left',
		},
		{
			title: t('periods.columns.delete'),
			dataIndex: 'name',
			key: 'name',
			width: '10%',
			align: 'center',
			render: (value, record) => (
				<Row className="show-on-hover" justify="center">
					<Col span={6} onClick={() => handleEdit(record)}>
						<EditOutlined />
					</Col>
					<Col span={6} className="delete" onClick={() => handleRemove(record?.id)}>
						<DeleteOutlined />
					</Col>
				</Row>
			),
		},
	];

	const handleInputKeyDown = () => {
		if (selectRef.current) {
			selectRef.current.blur();
			setPeriodModal(true);
		}
	};

	const handleRemove = (id) => {
		let url = `${SERVER_IP}api/period/${id}?branchId=${globalRedux?.selectedBranch?.id}`;
		dispatch(deleteApi('DELETE_PERIOD', url));
	};

	const handleSubmit = (values) => {
		console.log('🚀 ~ handleSubmit ~ values:', values);
		let data = {
			...values,
			branchId: globalRedux?.selectedBranch?.id,
		};
		if (editData) {
			let url = `${SERVER_IP}api/period/${editData.id}?branchId=${globalRedux?.selectedBranch?.id}`;
			dispatch(putApi(data, 'EDIT_PERIOD', url));
		} else {
			dispatch(postApi(data, 'ADD_PERIOD'));
		}
	};

	const handleEdit = (values) => {
		let data = {
			...values,
			branchId: globalRedux?.selectedBranch?.id,
			name: values?.name || '',
			days: values?.days || '',
		};
		setEditData(data);
	};

	const getPeriods = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId: globalRedux?.selectedBranch?.id,
		});
		let url = `${SERVER_IP}api/period?${string}`;
		dispatch(getApi('GET_PERIODS', url));
	}, [dispatch, globalRedux?.selectedBranch?.id]);

	useEffect(() => {
		getPeriods();
	}, [getPeriods]);

	useEffect(() => {
		if ((periods || [])?.length > 0) {
			setTableData(periods);
		}
	}, [periods]);

	useEffect(() => {
		if (editData) {
			form.setFieldsValue({
				name: editData?.name || '',
				days: editData?.days || '',
			});
			setAddFormVisible(true);
		}
	}, [editData, form]);

	useEffect(() => {
		if (!addFormVisible) {
			form.resetFields();
			setEditData(null);
		}
	}, [addFormVisible, form]);

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_PERIOD === 'SUCCESS' || globalRedux.apiStatus.EDIT_PERIOD === 'SUCCESS') {
			dispatch(resetApiStatus(editData ? 'EDIT_PERIOD' : 'ADD_PERIOD'));
			setAddFormVisible(false);
			getPeriods();
		}
	}, [globalRedux.apiStatus, editData, setPeriodModal, dispatch, getPeriods]);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_PERIOD === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_PERIOD'));
			doIt = true;
		}
		if (doIt) {
			getPeriods();
		}
	}, [globalRedux.apiStatus, dispatch, getPeriods]);

	const loading = globalRedux.apiStatus.GET_PERIODS === API_STATUS.PENDING;
	const buttonLoading = globalRedux.apiStatus.ADD_PERIOD === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_PERIOD === API_STATUS.PENDING;

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((period) => {
			return (
				(period?.name || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(period?.days || '')?.toString().toLowerCase().includes(searchKey.toLowerCase())
			);
		});
	}, [tableData, searchKey]);

	return (
		<>
			<Select
				onChange={onChange}
				ref={selectRef}
				placeholder="Select Period"
				style={{ width: '100%' }}
				showSearch
				optionFilterProp="children"
				filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
				{...{
					...(value && { value }),
				}}
				dropdownRender={(menu) => (
					<div>
						<div
							style={{ display: 'flex', flexWrap: 'nowrap', cursor: 'pointer', background: '#6ebaff' }}
							onClick={() => {
								handleInputKeyDown();
							}}>
							<a href style={{ flex: 'none', color: '#fff', padding: '8px', display: 'block' }}>
								Manage Period
							</a>
						</div>
						<Divider />
						{menu}
					</div>
				)}>
				{periods.map((period) => (
					<Select.Option value={period.days}>{`${period?.name} - ${period?.days} days`}</Select.Option>
				))}
			</Select>

			<Modal
				title={
					<Row>
						<Col span={24}>
							<Row>
								<Col span={24}>Periods</Col>
							</Row>
						</Col>
					</Row>
				}
				footer={null}
				onCancel={() => setPeriodModal(false)}
				cancelButtonProps={{ onPress: () => setPeriodModal(false) }}
				okText="Save"
				open={periodModal}
				style={{ top: 0 }}
				width={'40%'}>
				<Row className="add-period">
					<Col span={24}>
						{addFormVisible && (
							<Row className="add-period-form">
								<Col span={24}>
									<Form
										name="add-staff"
										style={{}}
										requiredMark={false}
										colon={false}
										labelAlign="left"
										form={form}
										onFinish={handleSubmit}
										{...layer1FormCol}>
										<Row gutter={[10, 0]}>
											<Col span={15}>
												<Form.Item
													label="Period Name"
													name="name"
													initialValue={editData?.name}
													rules={[
														{
															required: true,
															message: 'This Field is Required!',
														},
													]}>
													<Input autoFocus placeholder="Period name" />
												</Form.Item>
											</Col>
											<Col span={9}>
												<Form.Item
													label="Days"
													name="days"
													initialValue={editData?.days}
													rules={[
														{
															required: true,
															message: 'This Field is Required!',
														},
													]}>
													<Input placeholder="Days" />
												</Form.Item>
											</Col>
										</Row>
										<Row gutter={[10, 0]}>
											<Col span={12}>
												<Form.Item
													style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}
													wrapperCol={{
														span: 24,
													}}>
													<Row>
														<Button loading={buttonLoading} type="primary" style={{ marginRight: 5 }} htmlType="submit">
															{editData ? 'Update' : 'Save'}
														</Button>
														<Button onClick={() => setAddFormVisible(false)} style={{}} danger>
															Cancel
														</Button>
													</Row>
												</Form.Item>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						)}
						<TableComponent
							className="add-table"
							style={{ width: '100%' }}
							rowKey={(record) => record.id}
							dataSource={filteredData}
							columns={column}
							pagination={false}
							loading={loading}
							title={() =>
								addFormVisible ? null : (
									<Row gutter={[10, 0]} style={{ justifyContent: 'space-between' }}>
										<Col span={15}>
											<Input
												placeholder="Search Period"
												suffix={<AiOutlineSearch />}
												onChange={({ target: { value } }) => setSearchKey(value)}
											/>
										</Col>
										<Col span={9} style={{ textAlign: 'right' }}>
											<Button type="primary" onClick={() => setAddFormVisible(true)}>
												{t('periods.create_period')}
											</Button>
										</Col>
									</Row>
								)
							}
						/>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default PeriodModal;
